/**
 * Protocol Detail Page
 * Shows PRS analysis with beautiful markdown rendering
 */

import React, {useEffect, useState, useCallback} from 'react';
import {
  Container,
  Typography,
  Paper,
  Button,
  Box,
  Alert,
  Chip,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import { ProgressOverlay } from '@signanthealth/modular-ui';
import {
  Psychology as AnalyzeIcon,
  Build as BuildIcon,
  Workspaces as WorkspaceIcon,
} from '@mui/icons-material';
import {useNavigate, useParams} from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {apiService} from '../services/api';
import {triggerAnalysis, triggerComparison, generatePlan} from '../services/agent';
import {AnalysisDialog} from '../components/AnalysisDialog';
import {BuildPlanDialog} from '../components/BuildPlanDialog';
import {PlanInputDialog} from '../components/PlanInputDialog';
import {PlanActionsTable} from '../components/PlanActionsTable';
import {RequirementsEditor} from '../components/RequirementsEditor';
import {TrialTopBar} from '../components/TrialTopBar';
import {PlatformTabsLayout} from '../components/PlatformTabsLayout';
import {PRSAnalysisDialog} from '../components/PRSAnalysisDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const {children, value, index, ...other} = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{p: 3}}>{children}</Box>}
    </div>
  );
}

const ProtocolDetail: React.FC = () => {
  const navigate = useNavigate();
  const {trialId, version} = useParams<{trialId: string; version: string}>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [prsData, setPrsData] = useState<any>(null);
  const [cachedAnalysis, setCachedAnalysis] = useState<any>(null);
  const [cachedComparison, setCachedComparison] = useState<any>(null);
  const [isBaseVersion, setIsBaseVersion] = useState<boolean>(true); // Assume true until we know
  const [baseVersion, setBaseVersion] = useState<string>(''); // Store base version
  const [tabValue, setTabValue] = useState(0);
  const [trialPlatforms, setTrialPlatforms] = useState<('app' | 'web')[]>(['app']); // Default to app
  const [baseRequirements, setBaseRequirements] = useState<any>(null); // Requirements for base PRS

  // Analysis Dialog state
  const [analysisDialogOpen, setAnalysisDialogOpen] = useState(false);
  const [analysisJobId, setAnalysisJobId] = useState<string | null>(null);
  const [comparisonVersion2, setComparisonVersion2] = useState<string | undefined>(undefined);

  // Build Plan Dialog state
  const [buildPlanDialogOpen, setBuildPlanDialogOpen] = useState(false);
  const [currentDialogPlatform, setCurrentDialogPlatform] = useState<'app' | 'web'>('app');
  const [buildPlanJobId, setBuildPlanJobId] = useState<string | null>(null);

  // Plan Input Dialog state
  const [planInputDialogOpen, setPlanInputDialogOpen] = useState(false);

  // Cached Plan state (for Plan tab)
  const [cachedPlan, setCachedPlan] = useState<any>(null);

  // PRS Analysis Dialog state (for base PRS detailed view)
  const [prsAnalysisDialogOpen, setPrsAnalysisDialogOpen] = useState(false);

  const loadPRSData = useCallback(async () => {
    if (!trialId || !version) return;

    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getPRSContent(trialId, version);
      setPrsData(data);

      // Check if this is the base/current version
      try {
        const versions = await apiService.listPRSVersions(trialId, 'app');
        const versionInfo = versions[version];
        if (versionInfo) {
          setIsBaseVersion(versionInfo.is_base || false);
        }
        // Store the base version for plan generation
        const baseVer = Object.entries(versions).find(([_, data]: [string, any]) => data.is_base)?.[0];
        if (baseVer) {
          setBaseVersion(baseVer);
        }
      } catch (err) {
        console.log('Could not determine if base version:', err);
      }

      // Fetch trial platforms for multi-platform UI
      try {
        const appProtocols = await apiService.listProtocols('app').catch(() => []);
        const webProtocols = await apiService.listProtocols('web').catch(() => []);
        const trialPlatformsSet = new Set<'app' | 'web'>();

        [...appProtocols, ...webProtocols].forEach((protocol: any) => {
          if (protocol.trial_id === trialId) {
            trialPlatformsSet.add(protocol.platform as 'app' | 'web');
          }
        });

        setTrialPlatforms(Array.from(trialPlatformsSet).length > 0 ? Array.from(trialPlatformsSet) : ['app']);
      } catch (err) {
        console.log('Could not fetch trial platforms, defaulting to app:', err);
        setTrialPlatforms(['app']);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load PRS data');
    } finally {
      setLoading(false);
    }
  }, [trialId, version]);

  const checkCachedAnalysis = useCallback(async () => {
    if (!trialId || !version) return;

    // If this is the base version, check for cached analysis
    if (isBaseVersion) {
      try {
        const body = await apiService.getCachedAnalysis(trialId, version);

        if (body.success && body.cached && body.analysis) {
          setCachedAnalysis(body.analysis);
          setTabValue(0); // Show analysis tab first
        }
      } catch (err) {
        console.log('No cached analysis available');
      }
    } else {
      // If not base version, check for cached comparison
      try {
        const versions = await apiService.listPRSVersions(trialId, 'app');
        const baseVersion = Object.entries(versions).find(([_, data]) => data.is_base)?.[0];

        if (baseVersion) {
          const body = await apiService.getCachedComparison(trialId, baseVersion, version);

          if (body.success && body.cached && body.comparison) {
            // Extract the comparison text (it might be a string or an object)
            const comparisonText = typeof body.comparison === 'string'
              ? body.comparison
              : body.comparison.comparison_text || JSON.stringify(body.comparison, null, 2);
            setCachedComparison(comparisonText);
            setTabValue(0); // Show comparison in analysis tab
          }
        }
      } catch (err) {
        console.log('No cached comparison available');
      }
    }
  }, [trialId, version, isBaseVersion]);

  useEffect(() => {
    loadPRSData();
  }, [trialId, version, loadPRSData]);

  // Reset tab value when switching between base and comparison views
  useEffect(() => {
    setTabValue(0); // Always start at first tab
  }, [isBaseVersion]);

  // Run checkCachedAnalysis after we know isBaseVersion
  useEffect(() => {
    checkCachedAnalysis();
  }, [isBaseVersion, checkCachedAnalysis]);

  // Check for cached plan (only for non-base versions)
  const checkCachedPlan = useCallback(async () => {
    if (!trialId || !version || isBaseVersion) {
      setCachedPlan(null);
      return;
    }

    try {
      const versions = await apiService.listPRSVersions(trialId, 'app');
      const base = Object.entries(versions).find(([_, data]) => data.is_base)?.[0];

      if (base) {
        // Check if plan is cached for this comparison
        const body = await apiService.getCachedPlan(trialId, base, version, 'app');

        // Sessions API returns plan data directly, not wrapped
        if (body && body.plan_id) {
          setCachedPlan(body);
        }
      }
    } catch (err) {
      console.log('No cached plan available');
    }
  }, [trialId, version, isBaseVersion]);

  // Run checkCachedPlan after we know isBaseVersion
  useEffect(() => {
    checkCachedPlan();
  }, [isBaseVersion, checkCachedPlan]);

  // Check for cached requirements (only for base versions)
  const checkBaseRequirements = useCallback(async () => {
    if (!trialId || !version || !isBaseVersion) {
      setBaseRequirements(null);
      return;
    }

    try {
      // For base PRS, session ID is just the version itself (e.g., "2-2")
      // We need to call the API directly with the correct session ID format
      const sessionId = version; // NOT "version_to_version"
      const response = await apiService['client'].get(
        `/protocol/${trialId}/sessions/${sessionId}/app/requirements`,
        {params: {platform: 'app'}}
      );

      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;

      console.log('📋 Base Requirements API response:', body);

      if (body && body.requirements) {
        console.log('✅ Base requirements found:', body.requirements);
        setBaseRequirements(body);
      } else {
        console.log('⚠️ No base requirements found');
        setBaseRequirements(null);
      }
    } catch (err) {
      console.log('No cached requirements for base PRS:', err);
      setBaseRequirements(null);
    }
  }, [trialId, version, isBaseVersion]);

  // Run checkBaseRequirements after we know isBaseVersion
  useEffect(() => {
    checkBaseRequirements();
  }, [isBaseVersion, checkBaseRequirements]);

  const handleAnalyze = async (platform: 'app' | 'web' = 'app') => {
    if (!trialId || !version) return;

    try {
      let response;

      if (!isBaseVersion) {
        // For non-base versions, trigger comparison with base version
        const versions = await apiService.listPRSVersions(trialId, platform);
        const baseVersion = Object.entries(versions).find(([_, data]) => data.is_base)?.[0];

        if (baseVersion) {
          response = await triggerComparison({
            trial_id: trialId,
            version1: baseVersion,
            version2: version,
            platform: platform,
            force: true,
          });
          setComparisonVersion2(version); // Store for dialog
        } else {
          setError('Could not find base version for comparison');
          return;
        }
      } else {
        // For base version, trigger analysis + requirements generation
        response = await triggerAnalysis({
          trial_id: trialId,
          version: version,
          platform: platform,
          force: true,
          generate_requirements: true,  // Generate requirements from single PRS analysis
        });
        setComparisonVersion2(undefined); // Clear for analysis
      }

      setAnalysisJobId(response.job_id);
      setAnalysisDialogOpen(true);
    } catch (err: any) {
      setError(`Failed to start analysis: ${err.message || 'Unknown error'}`);
    }
  };

  const handleCloseAnalysisDialog = () => {
    setAnalysisDialogOpen(false);
    setAnalysisJobId(null);
    setComparisonVersion2(undefined);
    // Refresh to get new cached analysis
    checkCachedAnalysis();
    // Also check for requirements (base PRS)
    checkBaseRequirements();
  };

  const handleGeneratePlan = (platform: 'app' | 'web' = 'app') => {
    // Store platform for use in callback
    setCurrentDialogPlatform(platform);
    // Open the PlanInputDialog to collect optional context
    setPlanInputDialogOpen(true);
  };

  const handleGeneratePlanWithContext = async (analysisContext: string, planInstructions: string) => {
    if (!trialId || !version) return;

    // Close the input dialog
    setPlanInputDialogOpen(false);

    try {
      // Get the base version to compare against
      const versions = await apiService.listPRSVersions(trialId, currentDialogPlatform);
      const baseVersion = Object.entries(versions).find(([_, data]) => data.is_base)?.[0];

      if (!baseVersion) {
        setError('Could not find base version for plan generation');
        return;
      }

      // Generate plan: base version → current version (with optional context)
      // Note: This ALWAYS generates fresh (no cache check)
      const response = await generatePlan({
        trial_id: trialId,
        version1: baseVersion,
        version2: version,
        platform: currentDialogPlatform,
        analysis_context: analysisContext || undefined,
        plan_instructions: planInstructions || undefined,
      });

      setBuildPlanJobId(response.job_id);
      setBuildPlanDialogOpen(true);
    } catch (err: any) {
      setError(`Failed to start plan generation: ${err.message || 'Unknown error'}`);
    }
  };

  const handleCloseBuildPlanDialog = () => {
    setBuildPlanDialogOpen(false);
    setBuildPlanJobId(null);
    // Refresh to check if plan is now cached
    checkCachedPlan();
  };

  if (loading) {
    return <ProgressOverlay inProgress={true} fullOverlay message="Loading protocol..." />;
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{mt: 4, mb: 4}}>
        <TrialTopBar
          trialId={trialId || ''}
          version={version || ''}
          onBack={() => navigate('/')}
          viewType="analysis"
        />
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  // PRS data is flat, not nested under 'content'
  const content = prsData || {};

  // Render content for each platform tab
  const renderPlatformContent = (platform: 'app' | 'web') => {
    if (platform === 'web') {
      // WEB placeholder for demo
      return (
        <Paper sx={{p: 4, textAlign: 'center'}}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            WEB Platform Analysis
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Analysis for WEB platform coming soon
          </Typography>
        </Paper>
      );
    }

    // APP platform - existing full functionality
    return (
      <>
        {/* Action Buttons */}
        <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2}}>
          {/* For base PRS: show "View PRS Analysis" button if we have base requirements */}
          {isBaseVersion && baseRequirements && (
            <Button
              variant="outlined"
              color="info"
              onClick={() => setPrsAnalysisDialogOpen(true)}
            >
              View PRS Analysis
            </Button>
          )}

          <Button
            variant="contained"
            startIcon={<AnalyzeIcon />}
            onClick={() => handleAnalyze(platform)}
          >
            {(isBaseVersion ? (baseRequirements ? 'Re-Analyze PRS' : 'Analyze PRS') : 'Re-Analyze')}
          </Button>

          {!isBaseVersion && (
            <>
              <Button
                variant="outlined"
                color="success"
                startIcon={<BuildIcon />}
                onClick={() => handleGeneratePlan(platform)}
              >
                Generate Plan
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<WorkspaceIcon />}
                onClick={() => {
                  // Navigate to workspace with derived session ID
                  const sessionId = baseVersion ? `${baseVersion}_to_${version}` : version;
                  navigate(`/workspace/${trialId}/${sessionId}/${platform}`);
                }}
              >
                Workspace
              </Button>
            </>
          )}
        </Box>

        {/* Status Badge */}
        {cachedAnalysis && (
          <Box sx={{mb: 2}}>
            <Chip
              label="AI Analyzed"
              color="success"
              icon={<AnalyzeIcon />}
            />
          </Box>
        )}

        {/* Metadata */}
        <Paper
          elevation={3}
          sx={{
            p: 2,
            mb: 3,
            border: 1, borderColor: 'divider',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          {content.protocol_number && (
            <Typography variant="body2" color="text.secondary">
              Protocol: {content.protocol_number}
            </Typography>
          )}
          {content.customer_name && (
            <Typography variant="body2" color="text.secondary">
              Customer: {content.customer_name}
            </Typography>
          )}
          {content.uploaded_at && (
            <Typography variant="caption" color="text.secondary">
              Uploaded: {new Date(content.uploaded_at).toLocaleString()}
            </Typography>
          )}
        </Paper>

        {/* Content Area - conditional based on view type */}
        {isBaseVersion ? (
          /* Base PRS View: Show Requirements (if available) */
          baseRequirements ? (
            <Paper
              elevation={3}
              sx={{
                mb: 3,
                p: 3,
                border: 1, borderColor: 'divider',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
                PRS Requirements
              </Typography>
              <RequirementsEditor requirementsData={baseRequirements} />
            </Paper>
          ) : (
            /* No Requirements Yet */
            <Paper
              elevation={3}
              sx={{
                mb: 3,
                p: 4,
                textAlign: 'center',
                border: 1, borderColor: 'divider',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Requirements Available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click "Analyze PRS" to generate requirements
              </Typography>
            </Paper>
          )
        ) : (
          /* Comparison View: Show tabs (Comparison + Plan) */
          <Paper
            elevation={3}
            sx={{
              mb: 3,
              border: 1, borderColor: 'divider',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            <Tabs
              value={tabValue}
              onChange={(_, newValue) => setTabValue(newValue)}
              variant="fullWidth"
            >
              <Tab key="comparison" label={cachedComparison ? `Comparison ✓` : `Comparison`} />
              <Tab key="plan" label={cachedPlan ? `Plan ✓` : `Plan`} disabled={!cachedPlan} />
            </Tabs>

            <Divider />

          {/* Tab 0: AI Analysis */}
          <TabPanel value={tabValue} index={0}>
            {cachedAnalysis || cachedComparison ? (
              <Box>
                <Chip
                  label={cachedComparison ? "Cached Comparison" : "Cached Analysis"}
                  size="small"
                  color="success"
                  sx={{mb: 2}}
                />
                <Box
                  sx={{
                    '& h1': {fontSize: '2rem', fontWeight: 600, mb: 2, mt: 3},
                    '& h2': {fontSize: '1.5rem', fontWeight: 600, mb: 1.5, mt: 2, color: 'primary.main'},
                    '& h3': {fontSize: '1.25rem', fontWeight: 600, mb: 1, mt: 1.5},
                    '& p': {mb: 1.5, lineHeight: 1.7},
                    '& ul, & ol': {mb: 1.5, pl: 3},
                    '& li': {mb: 0.5},
                    '& code': {
                      backgroundColor: 'action.hover',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      fontFamily: 'monospace',
                      fontSize: '0.9em',
                    },
                    '& pre': {
                      backgroundColor: 'action.hover',
                      padding: 2,
                      borderRadius: 1,
                      overflow: 'auto',
                    },
                    '& strong': {fontWeight: 600},
                    '& table': {
                      borderCollapse: 'collapse',
                      width: '100%',
                      mb: 2,
                    },
                    '& th, & td': {
                      border: 1, borderColor: 'divider',
                      padding: '8px',
                      textAlign: 'left',
                    },
                    '& th': {
                      backgroundColor: 'action.hover',
                      fontWeight: 600,
                    },
                  }}
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {cachedComparison
                      ? (typeof cachedComparison === 'string'
                        ? cachedComparison
                        : cachedComparison.comparison_text || JSON.stringify(cachedComparison, null, 2))
                      : (cachedAnalysis.analysis_text || JSON.stringify(cachedAnalysis, null, 2))}
                  </ReactMarkdown>
                </Box>
              </Box>
            ) : (
              <Box sx={{textAlign: 'center', py: 4}}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {isBaseVersion ? 'No AI Analysis Available' : 'No Comparison Available'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                  {isBaseVersion
                    ? 'Run an AI analysis to get comprehensive insights about this PRS'
                    : 'Compare this version with the current version to see what changed'}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AnalyzeIcon />}
                  onClick={() => handleAnalyze(platform)}
                >
                  {isBaseVersion ? 'Analyze with AI' : 'Compare with Current'}
                </Button>
              </Box>
            )}
          </TabPanel>

          {/* Tab 1: Plan tab (comparison view only) */}
          <TabPanel value={tabValue} index={1}>
            {cachedPlan ? (
              <PlanActionsTable
                plan={typeof cachedPlan === 'string' ? JSON.parse(cachedPlan) : cachedPlan}
                cached={true}
              />
            ) : (
              <Box sx={{textAlign: 'center', py: 4}}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Plan Available
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                  Generate a modification plan to see surgical changes required
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<BuildIcon />}
                  onClick={() => handleGeneratePlan(platform)}
                >
                  Generate Plan
                </Button>
              </Box>
            )}
          </TabPanel>
        </Paper>
        )}

        {/* PRS Analysis Dialog - shows detailed tabs for base PRS */}
        <PRSAnalysisDialog
          open={prsAnalysisDialogOpen}
          onClose={() => setPrsAnalysisDialogOpen(false)}
          trialId={trialId || ''}
          version={version || ''}
          cachedAnalysis={cachedAnalysis}
          cachedRequirements={baseRequirements}
        />

      </>
    );
  };

  return (
    <Container maxWidth="lg" sx={{mt: 4, mb: 4}}>
      {/* Trial Top Bar */}
      <TrialTopBar
        trialId={trialId || ''}
        version={version || ''}
        onBack={() => navigate('/')}
        viewType="analysis"
      />

      {/* Platform Tabs Layout */}
      <PlatformTabsLayout
        platforms={trialPlatforms}
        renderTabContent={renderPlatformContent}
        trialId={trialId || ''}
        version={version || ''}
      />

      {/* Dialogs */}
      <AnalysisDialog
        open={analysisDialogOpen}
        onClose={handleCloseAnalysisDialog}
        jobId={analysisJobId}
        trialId={trialId || ''}
        version={version || ''}
        version2={comparisonVersion2}
      />

      <PlanInputDialog
        open={planInputDialogOpen}
        onClose={() => setPlanInputDialogOpen(false)}
        onGenerate={handleGeneratePlanWithContext}
        trialId={trialId || ''}
        version1={baseVersion}
        version2={version || ''}
      />

      <BuildPlanDialog
        open={buildPlanDialogOpen}
        onClose={handleCloseBuildPlanDialog}
        jobId={buildPlanJobId}
        trialId={trialId || ''}
        version1={baseVersion}
        version2={version || ''}
      />
    </Container>
  );
};

export default ProtocolDetail;
